import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const QR_SCRIPT_ID = 'qrcodejs-cdn-script';

const SellerTableQ = () => {
  const [loading, setLoading] = useState(true);
  const [shopId, setShopId] = useState(0);
  const [shopName, setShopName] = useState('');
  const [tables, setTables] = useState([]);
  const [error, setError] = useState('');
  const [qrReady, setQrReady] = useState(false);

  const qrRefs = useRef({});

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        const res = await axios.get('/api/seller/table/q-data', {
          withCredentials: true,
        });

        if (!mounted) return;

        const data = res.data;

        if (!data || data.ok !== true) {
          setError(data?.error || data?.msg || '데이터를 불러오지 못했습니다.');
          setLoading(false);
          return;
        }

        setShopId(parseInt(data.shop_id || 0, 10));
        setShopName(data.shop_name || '');
        setTables(Array.isArray(data.tables) ? data.tables : []);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;

        if (err.response && err.response.status === 401) {
          const redirectUrl = err.response?.data?.redirect || '/seller/manage';
          window.location.href = redirectUrl;
          return;
        }

        setError(
          err.response?.data?.error ||
          err.response?.data?.msg ||
          '서버 오류가 발생했습니다.'
        );
        setLoading(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadQrScript = () => {
      if (window.QRCode) {
        setQrReady(true);
        return;
      }

      const existing = document.getElementById(QR_SCRIPT_ID);
      if (existing) {
        existing.addEventListener('load', () => {
          if (mounted) setQrReady(true);
        });
        existing.addEventListener('error', () => {
          if (mounted) setError('QR 라이브러리를 불러오지 못했습니다.');
        });
        return;
      }

      const script = document.createElement('script');
      script.id = QR_SCRIPT_ID;
      script.src = 'https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js';
      script.async = true;

      script.onload = () => {
        if (mounted) setQrReady(true);
      };

      script.onerror = () => {
        if (mounted) setError('QR 라이브러리를 불러오지 못했습니다.');
      };

      document.body.appendChild(script);
    };

    loadQrScript();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!qrReady) return;
    if (!tables.length) return;
    if (!window.QRCode) return;

    tables.forEach((t) => {
      const el = qrRefs.current[t.id];
      if (!el) return;

      el.innerHTML = '';

      new window.QRCode(el, {
        text: t.url,
        width: 170,
        height: 170,
      });
    });
  }, [loading, qrReady, tables]);

  return (
    <>
      <style>{`
        .card-q {
            border: 1px solid #e9ecef;
            border-radius: 14px;
        }

        .qr-wrap {
            width: 180px;
            height: 180px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 1px dashed #ced4da;
            border-radius: 12px;
            background: #fff;
        }

        .qr-box {
            width: 170px;
            height: 170px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .qr-box img,
        .qr-box canvas {
            display: block;
        }

        @media print {
            .no-print { display: none !important; }
            .print-grid { gap: 10px !important; }
            .qr-wrap { border: 0 !important; }
            body { background: #fff !important; }
        }
        `}</style>

      <div className="container py-3 bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3 no-print">
          <div>
            <h5 className="m-0">테이블 QR 생성</h5>
            <div className="text-muted small">
              {shopName} (shop_id={shopId})
            </div>
          </div>

          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-dark btn-sm"
              onClick={() => window.print()}
            >
              인쇄
            </button>
            <a
              className="btn btn-outline-secondary btn-sm"
              href="/pos/floor/editor"
            >
              테이블 편집
            </a>
            <a
              className="btn btn-outline-secondary btn-sm"
              href="/pos"
            >
              POS
            </a>
          </div>
        </div>

        {loading ? null : error ? (
          <div className="alert alert-warning">{error}</div>
        ) : !tables.length ? (
          <div className="alert alert-warning">
            활성 테이블이 없습니다. 먼저 /pos/floor/editor 에서 테이블을 추가하세요.
          </div>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 print-grid">
            {tables.map((t) => (
              <div className="col" key={t.id}>
                <div className="card-q p-3">
                  <div className="d-flex gap-3 align-items-center">
                    <div className="qr-wrap">
                      <div
                        className="qr-box"
                        ref={(el) => {
                          if (el) qrRefs.current[t.id] = el;
                        }}
                      />
                    </div>

                    <div className="flex-grow-1">
                      <div
                        className="fw-bold"
                        style={{ fontSize: '18px', lineHeight: '1.1' }}
                      >
                        {t.name}
                      </div>

                      <div className="text-muted small mt-1">
                        table_id: {t.id}
                      </div>

                      <div
                        className="small mt-2"
                        style={{ wordBreak: 'break-all' }}
                      >
                        {t.url}
                      </div>

                      <div className="no-print mt-2">
                        <a
                          className="btn btn-outline-dark btn-sm"
                          target="_blank"
                          rel="noreferrer"
                          href={t.url}
                        >
                          열기
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="text-muted small mt-2">
                    * 손님이 QR 스캔 → 메뉴 담기 → 주문하기(주문 저장)
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SellerTableQ;